import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { FlagReason } from "@/lib/interaction-types";

interface InteractionCacheEntry {
  id: string;
  isFlagged: boolean;
  flagReasons: FlagReason[] | null;
  [key: string]: unknown;
}

export function useToggleHighlight(interactionId: string, animalId?: string) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async () => {
      return apiRequest(
        "PATCH",
        `/api/interactions/${encodeURIComponent(interactionId)}/highlight`
      ) as Promise<{ isFlagged: boolean; flagReasons: FlagReason[] }>;
    },
    onSuccess: (data) => {
      // Seed or update the per-interaction cache entry so any active subscriber
      // (e.g. InteractionDetailDrawer) picks up the new state immediately.
      queryClient.setQueryData<InteractionCacheEntry>(
        ["/api/interactions", interactionId],
        (old) =>
          old
            ? { ...old, ...data }
            : { id: interactionId, isFlagged: data.isFlagged, flagReasons: data.flagReasons }
      );

      const isNowHighlighted = (data.flagReasons ?? []).some(
        (r) => r.type === "highlighted"
      );
      toast({
        title: isNowHighlighted ? "Interaction highlighted" : "Highlight removed",
      });
    },
    onError: () => {
      toast({
        title: "Could not update highlight. Please try again.",
        variant: "destructive",
      });
    },
    onSettled: () => {
      // Refresh all interaction list queries so row data stays current.
      queryClient.invalidateQueries({ queryKey: ["/api/interactions"] });
      // Refresh the highlighted-count badge on the animal profile.
      if (animalId) {
        queryClient.invalidateQueries({
          queryKey: ["/api/animals", animalId, "interactions", "highlighted-count"],
        });
      }
    },
  });
}
